import React, { useState, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { Title, useDataProvider, useGetIdentity, useTranslate, useNotify, usePermissions } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useBranding } from './contexts/BrandingContext';
import DashboardSettings from './components/Dashboard/DashboardSettings';
import { getDefaultPreferencesForRole, mergeCardOrder } from './components/Dashboard/dashboardConfig';
import RecommendedCoursesWidget from './components/Dashboard/RecommendedCoursesWidget';
import LearningPathsWidget from './components/Dashboard/LearningPathsWidget';
import StreakWidget from './components/Dashboard/StreakWidget';
import WeekWidget from './components/Dashboard/WeekWidget';
import PendingReviewsWidget from './components/Dashboard/PendingReviewsWidget';
import MyGroupsWidget from './components/Dashboard/MyGroupsWidget';
import MaterialSymbol from './components/common/MaterialSymbol';
import { tokenService } from './services/tokenService';
import './components/Dashboard/dashboard-design.css';

interface DashboardStats {
    wiki: { totalArticles: number; publishedArticles: number; draftArticles: number; totalViews: number; mostViewedArticles: any[]; recentArticles: any[]; };
    media: { totalFiles: number; totalFolders: number; totalSize: number; filesByType: { [key: string]: number }; recentFiles: any[]; };
    courses: { enrolled: number; inProgress: number; completed: number; completionRate: number; };
    certificates: number;
}

const Dashboard = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [preferences, setPreferences] = useState<any>({});
    const [cardOrder, setCardOrder] = useState<string[] | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [assignments, setAssignments] = useState<any[]>([]);
    const dataProvider = useDataProvider();
    const { data: identity } = useGetIdentity();
    const { permissions } = usePermissions();
    const navigate = useNavigate();
    const translate = useTranslate();
    const notify = useNotify();

    const userRoles = permissions || [];
    const { branding } = useBranding();

    useEffect(() => {
        // Ohne gültiges Token feuern wir die Dashboard-Loader nicht —
        // Login-Flow läuft noch oder Token ist abgelaufen. Sonst laufen die
        // Fetches mit leerem Authorization-Header auf und produzieren
        // "Failed to fetch" im Audit (Cluster E).
        if (!tokenService.hasValidToken()) return;
        loadStatistics();
        loadPreferences();
        loadAssignments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAssignments = async () => {
        try {
            const token = localStorage.getItem('token');
            // Get current user ID from JWT
            let currentUserId: number | null = null;
            if (token) {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    currentUserId = payload.userId ?? payload.user_id ?? null;
                } catch {}
            }
            if (!currentUserId) return;

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/course_assignments?userId=${currentUserId}&status[]=assigned&status[]=in_progress&order[dueDate]=asc`,
                { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' } }
            );
            if (response.ok) {
                const data = await response.json();
                const members = data['hydra:member'] ?? data['member'] ?? [];

                // Die API liefert targetType/targetId/targetTitle direkt — Pflichtaufgaben
                // können auf einen Kurs ODER einen Lernpfad zeigen. Wir mappen das auf eine
                // einheitliche target-Struktur für die Anzeige; bei alten Datensätzen ohne
                // targetTitle fällt die Kurs-IRI als Quelle zurück.
                const enriched = members.map((a: any) => {
                    const isPath = a.targetType === 'path';
                    let targetId = a.targetId ?? null;
                    if (!targetId && a.course) {
                        targetId = typeof a.course === 'object' ? a.course?.id
                            : typeof a.course === 'string' ? parseInt(a.course.split('/').pop() || '0')
                            : a.course;
                    }
                    return {
                        ...a,
                        target: {
                            type: isPath ? 'path' : 'course',
                            id: targetId,
                            title: a.targetTitle || `${isPath ? 'Lernpfad' : 'Kurs'} #${targetId}`,
                            link: isPath ? `/learning-paths/${targetId}` : `/course-player/${targetId}`,
                        },
                    };
                });

                setAssignments(enriched);
            }
        } catch (error) {
            console.error('Error loading assignments:', error);
        }
    };

    const loadPreferences = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard-preferences/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setPreferences(data.visibleCards || getDefaultPreferencesForRole(userRoles));
                setCardOrder(data.cardOrder || null);
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
            // Set role-based defaults
            setPreferences(getDefaultPreferencesForRole(userRoles));
            setCardOrder(null);
        }
    };

    const savePreferences = async (newPreferences: any, newCardOrder: string[]) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard-preferences/me`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ visibleCards: newPreferences, cardOrder: newCardOrder })
            });
            if (response.ok) {
                setPreferences(newPreferences);
                setCardOrder(newCardOrder);
                notify('Dashboard-Einstellungen gespeichert', { type: 'success' });
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
            notify('Fehler beim Speichern der Einstellungen', { type: 'error' });
        }
    };

    const loadStatistics = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        try {
            const [wikiArticles, wikiArticlesByViews, recentWikiArticles, mediaFiles, mediaFolders, recentMediaFiles] = await Promise.all([
                dataProvider.getList('wiki_articles', { pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' }, filter: {} }),
                dataProvider.getList('wiki_articles', { pagination: { page: 1, perPage: 5 }, sort: { field: 'viewCount', order: 'DESC' }, filter: { status: 'published' } }),
                dataProvider.getList('wiki_articles', { pagination: { page: 1, perPage: 5 }, sort: { field: 'createdAt', order: 'DESC' }, filter: {} }),
                dataProvider.getList('media_files', { pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' }, filter: {} }),
                dataProvider.getList('media_folders', { pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' }, filter: {} }),
                dataProvider.getList('media_files', { pagination: { page: 1, perPage: 5 }, sort: { field: 'createdAt', order: 'DESC' }, filter: {} })
            ]);

            const publishedCount = wikiArticles.data.filter((a: any) => a.status === 'published').length;
            const draftCount = wikiArticles.data.filter((a: any) => a.status === 'draft').length;
            const totalViews = wikiArticles.data.reduce((sum: number, a: any) => sum + (a.viewCount || 0), 0);
            const totalSize = mediaFiles.data.reduce((sum: number, f: any) => sum + (parseInt(f.size) || 0), 0);
            const filesByType: { [key: string]: number } = {};
            mediaFiles.data.forEach((f: any) => { const type = f.mimeType?.split('/')[0] || 'other'; filesByType[type] = (filesByType[type] || 0) + 1; });

            // Load course progress statistics from API
            let courseStats = { enrolled: 0, inProgress: 0, completed: 0, completionRate: 0 };
            try {
                const progressResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/progress/weekly`, { headers });
                if (progressResponse.ok) {
                    const progressData = await progressResponse.json();
                    courseStats = {
                        enrolled: progressData.totalStats?.totalEnrolled || 0,
                        inProgress: progressData.totalStats?.inProgress || 0,
                        completed: progressData.totalStats?.totalCompleted || 0,
                        completionRate: progressData.totalStats?.completionRate || 0
                    };
                }
            } catch (error) {
                console.error('Error loading course progress:', error);
            }

            // Load certificate count from API
            let certificateCount = 0;
            try {
                // #336: persönliche Kachel zeigt eigene Zertifikate (scope=me),
                // konsistent mit den user-gefilterten Geschwister-Kacheln.
                const certResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/certificates/count?scope=me`, { headers });
                if (certResponse.ok) {
                    const certData = await certResponse.json();
                    certificateCount = certData.count || 0;
                }
            } catch (error) {
                console.error('Error loading certificate count:', error);
            }

            const statsData: DashboardStats = {
                wiki: { totalArticles: wikiArticles.total || 0, publishedArticles: publishedCount, draftArticles: draftCount, totalViews, mostViewedArticles: wikiArticlesByViews.data, recentArticles: recentWikiArticles.data },
                media: { totalFiles: mediaFiles.total || 0, totalFolders: mediaFolders.total || 0, totalSize, filesByType, recentFiles: recentMediaFiles.data },
                courses: courseStats,
                certificates: certificateCount
            };

            setStats(statsData);
        } catch (error) {
            console.error('Error loading statistics:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Statistik-Kärtchen (.stat) — badgeClass steuert die Icon-Kachel
    // (warn = gelb/Status, gold/dark = per design-system.css neutral grau).
    const StatCard = ({ title, value, subtitle, icon, badgeClass, bar, onClick }: {
        title: string; value: React.ReactNode; subtitle?: string; icon: string;
        badgeClass?: string; bar?: number; onClick?: () => void;
    }) => (
        <div className="stat" onClick={onClick}>
            <span className={`badge${badgeClass ? ` ${badgeClass}` : ''}`}>
                <MaterialSymbol name={icon} size={22} />
            </span>
            <div>
                <div className="label">{title}</div>
                <div className="value">{value}</div>
                {subtitle && <div className="sub">{subtitle}</div>}
            </div>
            {bar !== undefined && <div className="bar"><i style={{ width: `${bar}%` }} /></div>}
        </div>
    );

    const QuickAccessCard = ({ title, description, icon, tone, onClick }: {
        title: string; description: string; icon: string; tone: 'green' | 'dark'; onClick: () => void;
    }) => (
        <div className="qcard" onClick={onClick}>
            <div className="top">
                <span className={`qico ${tone}`}><MaterialSymbol name={icon} size={22} /></span>
                <span className="arrow"><MaterialSymbol name="arrow_forward" size={20} /></span>
            </div>
            <h3>{title}</h3>
            <p>{description}</p>
        </div>
    );

    const ActivityPanel = ({ title, icon, iconTone, items, emptyMessage, renderItem }: {
        title: string; icon: string; iconTone?: 'green' | 'dark';
        items?: any[]; emptyMessage: string; renderItem: (item: any) => React.ReactNode;
    }) => (
        <div className="panel">
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className={`ico${iconTone ? ` ${iconTone}` : ''}`}><MaterialSymbol name={icon} size={19} /></span>
                    {title}
                </h2>
            </div>
            {items && items.length > 0 ? (
                items.slice(0, 5).map((item: any, index: number) => (
                    <React.Fragment key={item.id || index}>{renderItem(item)}</React.Fragment>
                ))
            ) : (
                <p className="empty-note">{emptyMessage}</p>
            )}
        </div>
    );

    const getDueSeverity = (dueDate: string): 'red' | 'amber' | 'green' => {
        const now = new Date();
        const due = new Date(dueDate);
        const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays <= 3) return 'red';
        if (diffDays <= 14) return 'amber';
        return 'green';
    };

    const getFileIcon = (mimeType?: string): string => {
        const type = mimeType?.split('/')[0];
        if (type === 'video') return 'smart_display';
        if (type === 'image') return 'image';
        return 'description';
    };

    const MandatoryAssignmentsWidget = () => {
        if (assignments.length === 0) return null;
        return (
            <div className="panel">
                <div className="sec-head">
                    <h2 className="sec-title">
                        <span className="ico"><MaterialSymbol name="check_box" size={19} /></span>
                        Meine Pflichtaufgaben
                        <span className="count-badge">{assignments.length}</span>
                    </h2>
                </div>
                {assignments.slice(0, 5).map((assignment: any) => {
                    const severity = getDueSeverity(assignment.dueDate);
                    const due = new Date(assignment.dueDate);
                    const now = new Date();
                    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const dueDateLabel = diffDays < 0
                        ? `${Math.abs(diffDays)} Tage überfällig`
                        : diffDays === 0
                            ? 'Heute fällig'
                            : `Noch ${diffDays} Tag(e)`;
                    const target = assignment.target ?? { type: 'course', id: assignment.course?.id, title: assignment.course?.title, link: `/course-player/${assignment.course?.id ?? ''}` };
                    const isPath = target.type === 'path';
                    return (
                        <div
                            key={assignment.id}
                            className={`assignrow due-${severity}`}
                            onClick={() => navigate(target.link)}
                        >
                            <div className="body">
                                <div className="tags-line">
                                    <span className="chip-out">{isPath ? 'Lernpfad' : 'Kurs'}</span>
                                    <span className="ttl">{target.title}</span>
                                    {assignment.recurrenceWeeks ? (
                                        <span className="chip-out">{`Wiederholung alle ${assignment.recurrenceWeeks} Wo.`}</span>
                                    ) : null}
                                </div>
                                {assignment.notes && <div className="notes">{assignment.notes}</div>}
                            </div>
                            <div className="due">
                                <span className="d1">
                                    {diffDays <= 3 && <MaterialSymbol name="warning" size={12} />}
                                    Fällig: {due.toLocaleDateString('de-DE')}
                                </span>
                                <span className="d2">{dueDateLabel}</span>
                            </div>
                        </div>
                    );
                })}
                {assignments.length > 5 && (
                    <p className="more-note">+ {assignments.length - 5} weitere Pflichtaufgaben</p>
                )}
            </div>
        );
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><CircularProgress sx={{ color: branding.primaryColor }} /></Box>;

    const firstName = (identity as any)?.firstName || translate('dashboard.defaultName');

    const effectiveOrder = mergeCardOrder(cardOrder, userRoles);

    // Map card keys to their render functions
    const renderCard = (key: string): React.ReactNode => {
        if (preferences[key] === false) return null;
        switch (key) {
            case 'myCourses':
                return <StatCard title={translate('dashboard.stats.myCourses')} value={stats?.courses.enrolled || 0} icon="school" onClick={() => navigate('/my-courses')} />;
            case 'inProgress':
                return <StatCard title={translate('dashboard.stats.inProgress')} value={stats?.courses.inProgress || 0} icon="hourglass_top" badgeClass="warn" onClick={() => navigate('/my-courses?filter=in_progress')} />;
            case 'completed':
                return <StatCard title={translate('dashboard.stats.completed')} value={stats?.courses.completed || 0} icon="check_circle" onClick={() => navigate('/my-courses?filter=completed')} />;
            case 'learningProgress':
                return stats?.courses ? (
                    <StatCard title="Gesamtfortschritt" value={`${stats.courses.completionRate}%`} icon="trending_up" bar={stats.courses.completionRate} onClick={() => navigate('/my-courses')} />
                ) : null;
            case 'certificates':
                return <StatCard title="Zertifikate" value={stats?.certificates || 0} icon="emoji_events" badgeClass="gold" onClick={() => navigate('/certificates')} />;
            case 'wikiArticles':
                return <StatCard title={translate('dashboard.stats.wikiArticles')} value={stats?.wiki.publishedArticles || 0} icon="article" onClick={() => navigate('/wiki')} />;
            case 'media':
                return <StatCard title={translate('dashboard.stats.media')} value={stats?.media.totalFiles || 0} subtitle={formatBytes(stats?.media.totalSize || 0)} icon="image" badgeClass="dark" onClick={() => navigate('/media-library')} />;
            case 'quickAccessMyCourses':
                return <QuickAccessCard title={translate('dashboard.quickAccessCards.myCourses.title')} description={translate('dashboard.quickAccessCards.myCourses.description')} icon="school" tone="green" onClick={() => navigate('/my-courses')} />;
            case 'quickAccessCatalogs':
                return <QuickAccessCard title={translate('dashboard.quickAccessCards.catalogs.title')} description={translate('dashboard.quickAccessCards.catalogs.description')} icon="folder" tone="green" onClick={() => navigate('/courses/catalogs')} />;
            case 'quickAccessCatalog':
                // Issue #153: Lerner-Katalog (/catalog) — getrennt von quickAccessCatalogs (Kursverwaltung).
                return <QuickAccessCard title={translate('dashboard.quickAccessCards.catalog.title')} description={translate('dashboard.quickAccessCards.catalog.description')} icon="explore" tone="green" onClick={() => navigate('/catalog')} />;
            case 'quickAccessWiki':
                return <QuickAccessCard title={translate('dashboard.quickAccessCards.wiki.title')} description={translate('dashboard.quickAccessCards.wiki.description')} icon="article" tone="dark" onClick={() => navigate('/wiki')} />;
            case 'quickAccessMedia':
                return <QuickAccessCard title={translate('dashboard.quickAccessCards.media.title')} description={translate('dashboard.quickAccessCards.media.description')} icon="image" tone="dark" onClick={() => navigate('/media-library')} />;
            case 'activityMostViewed':
                // Panel-Titel als deutsches Literal: die alten i18n-Keys tragen
                // Emojis (📈/🆕/📁), die im neuen Design den Sektions-Icons weichen.
                return <ActivityPanel title="Meist gelesene Artikel" icon="monitoring" items={stats?.wiki.mostViewedArticles} emptyMessage={translate('dashboard.activities.noArticles')} renderItem={(article: any) => (
                    <div className="listrow" onClick={() => navigate('/wiki/' + article.slug)}>
                        <div className="body">
                            <p className="ttl">{article.title}</p>
                            <div className="meta">
                                <MaterialSymbol name="visibility" size={14} />
                                {article.viewCount || 0} {translate('dashboard.activities.views')}
                            </div>
                        </div>
                    </div>
                )} />;
            case 'activityRecentArticles':
                return <ActivityPanel title="Neueste Artikel" icon="article" iconTone="green" items={stats?.wiki.recentArticles} emptyMessage={translate('dashboard.activities.noNewArticles')} renderItem={(article: any) => (
                    <div className="listrow" onClick={() => navigate('/wiki/' + article.slug)}>
                        <div className="body">
                            <p className="ttl">{article.title}</p>
                            <div className="meta">
                                {formatDate(article.createdAt)}
                                <span className={`status ${article.status === 'published' ? 'pub' : 'draft'}`}>
                                    {article.status === 'published' ? translate('dashboard.activities.published') : translate('dashboard.activities.draft')}
                                </span>
                            </div>
                        </div>
                    </div>
                )} />;
            case 'activityRecentFiles':
                return <ActivityPanel title="Neueste Dateien" icon="folder" iconTone="dark" items={stats?.media.recentFiles} emptyMessage={translate('dashboard.activities.noFiles')} renderItem={(file: any) => (
                    <div className="listrow">
                        <span className="fileico"><MaterialSymbol name={getFileIcon(file.mimeType)} size={20} /></span>
                        <div className="body">
                            <p className="ttl">{file.originalFilename}</p>
                            <div className="meta">{formatDate(file.createdAt)} · {formatBytes(parseInt(file.size) || 0)}</div>
                        </div>
                    </div>
                )} />;
            case 'mandatoryAssignments':
                return <MandatoryAssignmentsWidget />;
            case 'weeklyProgress':
                return <WeekWidget />;
            case 'learningPaths':
                return <LearningPathsWidget limit={3} onPathSelect={(pathId) => navigate(`/learning-paths/${pathId}`)} onViewAll={() => navigate('/learning-paths')} />;
            case 'gamification':
                return <StreakWidget />;
            case 'pendingReviews':
                return <PendingReviewsWidget limit={3} />;
            case 'myLearningGroups':
                return <MyGroupsWidget limit={3} />;
            case 'recommendedCourses':
                return <RecommendedCoursesWidget limit={4} />;
            default:
                return null;
        }
    };

    // Category assignments for each card key
    const cardCategory: Record<string, string> = {
        myCourses: 'stats', inProgress: 'stats', completed: 'stats', learningProgress: 'stats',
        certificates: 'stats', wikiArticles: 'stats', media: 'stats',
        quickAccessMyCourses: 'quickAccess', quickAccessCatalogs: 'quickAccess', quickAccessCatalog: 'quickAccess',
        quickAccessWiki: 'quickAccess', quickAccessMedia: 'quickAccess',
        activityMostViewed: 'activity', activityRecentArticles: 'activity', activityRecentFiles: 'activity',
        mandatoryAssignments: 'student', weeklyProgress: 'student', learningPaths: 'student',
        gamification: 'student', pendingReviews: 'student', myLearningGroups: 'student',
        recommendedCourses: 'student',
    };

    // Build grouped sections in order
    const orderedSections: { category: string; keys: string[] }[] = [];
    for (const key of effectiveOrder) {
        const cat = cardCategory[key] || 'student';
        const rendered = renderCard(key);
        if (!rendered) continue;
        const lastSection = orderedSections[orderedSections.length - 1];
        if (lastSection && lastSection.category === cat) {
            lastSection.keys.push(key);
        } else {
            orderedSections.push({ category: cat, keys: [key] });
        }
    }

    return (
        <div className="oa-dashboard">
            <Title title={translate('dashboard.title')} />

            {/* Welcome-Banner */}
            <section className="welcome">
                <button type="button" className="gear" onClick={() => setSettingsOpen(true)} aria-label="Dashboard anpassen">
                    <MaterialSymbol name="settings" size={20} />
                </button>
                <h2>{translate('dashboard.welcome', { firstName })}</h2>
                <p>{translate('dashboard.subtitle')}</p>
            </section>

            {orderedSections.map((section, sIdx) => {
                const visibleCards = section.keys.filter(key => renderCard(key) !== null);
                if (visibleCards.length === 0) return null;

                if (section.category === 'stats') {
                    return (
                        <section key={`section-${sIdx}`} className="stats">
                            {visibleCards.map(key => (
                                <React.Fragment key={key}>{renderCard(key)}</React.Fragment>
                            ))}
                        </section>
                    );
                }

                if (section.category === 'quickAccess') {
                    return (
                        <section key={`section-${sIdx}`} className="section-gap">
                            <div className="sec-head"><h2 className="sec-title">{translate('dashboard.quickAccess')}</h2></div>
                            <div className="quick">
                                {visibleCards.map(key => (
                                    <React.Fragment key={key}>{renderCard(key)}</React.Fragment>
                                ))}
                            </div>
                        </section>
                    );
                }

                if (section.category === 'activity') {
                    return (
                        <section key={`section-${sIdx}`} className="section-gap cols3">
                            {visibleCards.map(key => (
                                <React.Fragment key={key}>{renderCard(key)}</React.Fragment>
                            ))}
                        </section>
                    );
                }

                // Student cards render individually (full-width or paired)
                const pairableKeys = new Set(['learningPaths', 'gamification', 'pendingReviews', 'myLearningGroups']);
                const fullWidthCards: string[] = [];
                const pairableCards: string[] = [];
                for (const key of visibleCards) {
                    if (pairableKeys.has(key)) {
                        pairableCards.push(key);
                    } else {
                        fullWidthCards.push(key);
                    }
                }

                return (
                    <React.Fragment key={`section-${sIdx}`}>
                        {fullWidthCards.map(key => (
                            <section key={key} className="section-gap">
                                {renderCard(key)}
                            </section>
                        ))}
                        {pairableCards.length > 0 && (
                            <section className={`section-gap${pairableCards.length > 1 ? ' cols2' : ''}`}>
                                {pairableCards.map(key => (
                                    <React.Fragment key={key}>{renderCard(key)}</React.Fragment>
                                ))}
                            </section>
                        )}
                    </React.Fragment>
                );
            })}

            <DashboardSettings
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                preferences={preferences}
                cardOrder={cardOrder}
                onSave={savePreferences}
            />
        </div>
    );
};

export default Dashboard;
